import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { CirclePlus } from "lucide-react";
import { useState, useEffect } from "react";
import { LoaderSpin } from "@/components/Loaders";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { generateReport, getReportTypes } from "@/Api/reports";
import { useToastNavigate } from "@/utils/toastNavigate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { getSelectUnities } from "@/Api/unities";
import { getSelectSaleTypes } from "@/Api/saleTypes";
import { getPaymentForms } from "@/Api/sales";
import { getSelectSellers } from "@/Api/sellers";
import { getSelectProductGroups } from "@/Api/productGroups";
import { SALE_STATUS, SALE_STATUS_LABELS } from "@/utils/constants";
import { usePermissions } from "@/hooks/usePermissions";
import { TypeUser } from "@/types/userType";
import { PERMISSIONS } from "@/constants/permissions";

const FINANCIAL_STATUS_OPTIONS = [
  { id: 1, name: "Aberto" }, // OPEN
  { id: 2, name: "Pago" }, // PAID
  { id: 3, name: "Cancelado" }, // CANCELED
  { id: 4, name: "Vencido" }, // EXPIRED
  { id: 7, name: "Editado e Aprovada" }, // EDITED_AND_APPROVED
];

const schema = z.object({
  name: z.string().min(1, { message: "*requerido" }),
  report_type_id: z.number({ required_error: "*requerido" }),

  start_date: z.string().optional(),
  end_date: z.string().optional(),

  payment_start_date: z.string().optional(),
  payment_end_date: z.string().optional(),

  due_start_date: z.string().optional(),
  due_end_date: z.string().optional(),

  competence_start_date: z.string().optional(),
  competence_end_date: z.string().optional(),

  unity_id: z.array(z.number()).optional(),
  sale_type_id: z.array(z.number()).optional(),
  payment_form_id: z.array(z.number()).optional(),
  seller_id: z
    .array(
      z.object({
        id: z.number(),
      })
    )
    .transform((value) => value.map((item) => item.id))
    .optional(),
  sale_status_id: z.array(z.number()).optional(),
  status_id: z.number().optional(),
  lottery_id: z.array(z.number()).optional(),
  client_type: z.enum(['all', 'new', 'old']).optional(),
  devolution_status_id: z.array(z.number()).optional(),
}).superRefine((data, ctx) => {
  // DRE Demonstrativo (7): período de competência obrigatório
  if (data.report_type_id !== 7) return;

  if (!data.competence_start_date) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["competence_start_date"],
      message: "*requerido",
    });
  }
  if (!data.competence_end_date) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["competence_end_date"],
      message: "*requerido",
    });
  }
  if (
    data.competence_start_date &&
    data.competence_end_date &&
    data.competence_end_date < data.competence_start_date
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["competence_end_date"],
      message: "A data final deve ser maior ou igual à inicial",
    });
  }
});

type SchemaType = z.infer<typeof schema>;

export const AddBtn = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [reportTypes, setReportTypes] = useState<any[]>([]);
  const [unities, setUnities] = useState<any[]>([]);
  const [saleTypes, setSaleTypes] = useState<any[]>([]);
  const [paymentForms, setPaymentForms] = useState<any[]>([]);
  const [sellers, setSellers] = useState<any[]>([]);
  const [lotteries, setLotteries] = useState<any[]>([]);
  const [formState, setFormState] = useState<string>("default");
  const { toast } = useToastNavigate();
  const { user } = usePermissions();

  // Relatório "Compras Clientes": Admin Geral sempre; dono da loteria e responsável de unidade
  // somente com a permissão no cargo; vendedor e estoquista nunca.
  // (Checagem local: o hasPermission global libera tudo para os demais tipos de usuário.)
  const checkIsAdmin = () => {
    if (!user) return false;
    if (user.user_type_id === TypeUser.admin) return true;
    if (user.user_type_id !== TypeUser.client && user.user_type_id !== TypeUser.unity_responsible) {
      return false;
    }
    return (user.roles || []).some((role) =>
      role.permissions?.some((permission) => permission.name === PERMISSIONS.VIEW_CUSTOMER_PURCHASES_REPORT)
    );
  };

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      report_type_id: undefined,
      start_date: "",
      end_date: "",
      payment_start_date: "",
      payment_end_date: "",
      due_start_date: "",
      due_end_date: "",
      competence_start_date: "",
      competence_end_date: "",
      unity_id: [],
      sale_type_id: [],
      payment_form_id: [],
      seller_id: [],
      sale_status_id: [],
      status_id: undefined,
      lottery_id: [],
      client_type: 'all',
      devolution_status_id: [],
    },
  });

  // Status de devolução
  const DEVOLUTION_STATUS_OPTIONS = [
    { id: 1, name: "Pendente" },
    { id: 2, name: "Aprovado" },
    { id: 3, name: "Rejeitado" },
  ];

  // Constantes para tipos de relatório
  const REPORT_TYPES = {
    FINANCIAL_ACCOUNTS_PAYABLE: 1,
    FINANCIAL_ACCOUNTS_RECEIVABLE: 2,
    FINANCIAL_CASH_FLOW: 3,
    FINANCIAL_SALES: 4,
    FINANCIAL_INCOME_STATEMENT_DRE: 5,
    PAYMENT_DATES_ONLY: 6,
    COMPETENCE_DATES_ONLY: 7,
    CUSTOMER_PURCHASES: 8,
    FINANCIAL_REFUNDS: 9,
  };

  // Status permitidos para Compras Clientes (apenas aprovadas, não canceladas)
  const CUSTOMER_PURCHASES_STATUS_OPTIONS = [
    { id: SALE_STATUS.APPROVED, label: SALE_STATUS_LABELS[SALE_STATUS.APPROVED] },
    { id: SALE_STATUS.EDITED_AND_APPROVED, label: SALE_STATUS_LABELS[SALE_STATUS.EDITED_AND_APPROVED] },
  ];

  const selectedReportType = form.watch("report_type_id");
  const isSalesReport = selectedReportType === REPORT_TYPES.FINANCIAL_SALES;
  const isPayableReport =
    selectedReportType === REPORT_TYPES.FINANCIAL_ACCOUNTS_PAYABLE;
  const isReceivableReport =
    selectedReportType === REPORT_TYPES.FINANCIAL_ACCOUNTS_RECEIVABLE;
  const isCashFlowReport =
    selectedReportType === REPORT_TYPES.FINANCIAL_CASH_FLOW;
  const isIncomeStatementReport =
    selectedReportType === REPORT_TYPES.FINANCIAL_INCOME_STATEMENT_DRE;
  const isPaymentDatesOnlyReport =
    selectedReportType === REPORT_TYPES.PAYMENT_DATES_ONLY;
  const isCompetenceDatesOnlyReport =
    selectedReportType === REPORT_TYPES.COMPETENCE_DATES_ONLY;
  const isCustomerPurchasesReport =
    selectedReportType === REPORT_TYPES.CUSTOMER_PURCHASES;
  const isRefundsReport =
    selectedReportType === REPORT_TYPES.FINANCIAL_REFUNDS;

  // Para ID 6: apenas datas de pagamento
  // Para ID 7: apenas datas de competência
  // Para ID 8: apenas start_date e end_date (intervalo simples)
  const needsPaymentDates =
    (isCashFlowReport ||
      isIncomeStatementReport ||
      isPayableReport ||
      isReceivableReport ||
      isPaymentDatesOnlyReport) &&
    !isCompetenceDatesOnlyReport &&
    !isCustomerPurchasesReport;

  const needsDueDates =
    (isCashFlowReport ||
      isIncomeStatementReport ||
      isPayableReport ||
      isReceivableReport) &&
    !isPaymentDatesOnlyReport &&
    !isCompetenceDatesOnlyReport &&
    !isCustomerPurchasesReport;

  const needsCompetenceDates =
    (isCashFlowReport ||
      isIncomeStatementReport ||
      isPayableReport ||
      isReceivableReport ||
      isCompetenceDatesOnlyReport) &&
    !isPaymentDatesOnlyReport &&
    !isCustomerPurchasesReport;

  const needsAdvancedFilters = (isCashFlowReport || isIncomeStatementReport) && !isCustomerPurchasesReport;

  useEffect(() => {
    // Limpar filtros de vendedor, status de venda e loteria quando não é Sales nem Compras Clientes
    if (!isSalesReport && !isCustomerPurchasesReport) {
      form.setValue("seller_id", []);
      form.setValue("sale_status_id", []);
      form.setValue("lottery_id", []);
    }

    if (!needsPaymentDates) {
      form.setValue("payment_start_date", "");
      form.setValue("payment_end_date", "");
    }
    if (!needsDueDates) {
      form.setValue("due_start_date", "");
      form.setValue("due_end_date", "");
    }
    if (!needsCompetenceDates) {
      form.setValue("competence_start_date", "");
      form.setValue("competence_end_date", "");
    }

    // Limpar filtros avançados quando não é Cash Flow, Income Statement, Sales ou Compras Clientes
    if (!needsAdvancedFilters && !isSalesReport && !isCustomerPurchasesReport) {
      form.setValue("unity_id", []);
      form.setValue("sale_type_id", []);
      form.setValue("payment_form_id", []);
    }

    if (
      !isPayableReport &&
      !isReceivableReport &&
      !isCashFlowReport &&
      !isIncomeStatementReport
    ) {
      form.setValue("status_id", undefined);
    }

    // Limpar filtro de status de devolução quando não é relatório de devoluções
    if (!isRefundsReport) {
      form.setValue("devolution_status_id", []);
    }
  }, [selectedReportType, form]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          reportTypesRes,
          unitiesRes,
          saleTypesRes,
          paymentFormsRes,
          sellersRes,
          lotteriesRes,
        ] = await Promise.all([
          getReportTypes(),
          getSelectUnities(),
          getSelectSaleTypes(),
          getPaymentForms(),
          getSelectSellers({ is_active: true }),
          getSelectProductGroups(),
        ]);

        setReportTypes(reportTypesRes.data?.response || []);
        setUnities(unitiesRes.data?.response || []);
        setSaleTypes(saleTypesRes.data?.response || []);
        setPaymentForms(paymentFormsRes.data?.response || []);
        setSellers(sellersRes.data?.response || []);
        setLotteries(lotteriesRes.data?.response || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const formSubmit = async (data: SchemaType) => {
    const hasDateFilter = !!(
      data.start_date ||
      data.end_date ||
      data.payment_start_date ||
      data.payment_end_date ||
      data.due_start_date ||
      data.due_end_date ||
      data.competence_start_date ||
      data.competence_end_date
    );

    const hasArrayFilter = !!(
      (data.unity_id && data.unity_id.length > 0) ||
      (data.sale_type_id && data.sale_type_id.length > 0) ||
      (data.payment_form_id && data.payment_form_id.length > 0) ||
      (data.seller_id && data.seller_id.length > 0) ||
      (data.sale_status_id && data.sale_status_id.length > 0) ||
      (data.lottery_id && data.lottery_id.length > 0)
    );

    const hasStatusFilter = !!data.status_id;

    if (!hasDateFilter && !hasArrayFilter && !hasStatusFilter) {
      toast({
        description: "Selecione pelo menos um filtro para gerar o relatório",
      });
      return;
    }

    setFormState("loading");

    const cleanedData = { ...data };

    if (
      !cleanedData.sale_status_id ||
      cleanedData.sale_status_id.length === 0
    ) {
      delete cleanedData.sale_status_id;
    }

    const { data: apiResponse, ok } = await generateReport(cleanedData);

    if (!ok) {
      setFormState("error");
      setTimeout(() => {
        setFormState("default");
      }, 1000);
      return;
    }
    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message || "Relatório gerado com sucesso!",
    });
    window.location.reload();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Gerar Relatório
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogDescription className="text-lg">
            Gerar Relatório
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>

        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <Form.Row>
            <Form.Field name="name">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Nome do Relatório <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Nome do relatório" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="report_type_id">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Tipo de Relatório <Form.Message />
                  </Form.Label>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value?.toString()}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {reportTypes
                        .filter((type) => {
                          // "Compras Clientes" (ID 8) só é visível para Admin
                          if (type.id === REPORT_TYPES.CUSTOMER_PURCHASES && !checkIsAdmin()) {
                            return false;
                          }
                          return true;
                        })
                        .map((type) => (
                          <SelectItem key={type.id} value={type.id.toString()}>
                            {type.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          {(isSalesReport || isCustomerPurchasesReport || isRefundsReport) && (
            <div className="grid grid-cols-2 gap-4">
              <Form.Row>
                <Form.Field name="start_date">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Data Inicial <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input type="date" {...field} />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <Form.Row>
                <Form.Field name="end_date">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Data Final <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input type="date" {...field} />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </div>
          )}

          {needsPaymentDates && (
            <div className="space-y-4">
              <h3 className="font-semibold mb-3">
                Filtros de Período de Pagamento
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="payment_start_date">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Data Inicial de Pagamento</Form.Label>
                        <Form.Control>
                          <Input type="date" {...field} />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
                <Form.Row>
                  <Form.Field name="payment_end_date">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Data Final de Pagamento</Form.Label>
                        <Form.Control>
                          <Input type="date" {...field} />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>
            </div>
          )}

          {needsDueDates && (
            <div className="space-y-4">
              <h3 className="font-semibold mb-3">
                Filtros de Período de Vencimento
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="due_start_date">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Data Inicial de Vencimento</Form.Label>
                        <Form.Control>
                          <Input type="date" {...field} />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
                <Form.Row>
                  <Form.Field name="due_end_date">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Data Final de Vencimento</Form.Label>
                        <Form.Control>
                          <Input type="date" {...field} />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>
            </div>
          )}

          {needsCompetenceDates && (
            <div className="space-y-4">
              <h3 className="font-semibold mb-3">
                Filtros de Período de Competência
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="competence_start_date">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Data Inicial de Competência <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input type="date" {...field} />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
                <Form.Row>
                  <Form.Field name="competence_end_date">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Data Final de Competência <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input type="date" {...field} />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>
            </div>
          )}

          {(isPayableReport ||
            isReceivableReport ||
            isCashFlowReport ||
            isIncomeStatementReport) && (
            <div className="space-y-4">
              <h3 className="font-semibold mb-3">Filtros Adicionais</h3>
              <Form.Row>
                <Form.Field name="status_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>Status da Transação</Form.Label>
                      <Select
                        onValueChange={(value) =>
                          field.onChange(
                            value === "all" ? undefined : Number(value)
                          )
                        }
                        value={field.value?.toString() || "all"}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Todos os status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os status</SelectItem>
                          {FINANCIAL_STATUS_OPTIONS.map((status) => (
                            <SelectItem
                              key={status.id}
                              value={status.id.toString()}
                            >
                              {status.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </div>
          )}

          {/* Filtros avançados para Cash Flow e Income Statement */}
          {needsAdvancedFilters && (
            <div className="space-y-4">
              <h3 className="font-semibold mb-3">Filtros Avançados</h3>

              <Form.Row>
                <Form.Field name="unity_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>Unidades</Form.Label>
                      <div className="space-y-2">
                        {/* Badges para mostrar seleções */}
                        {field.value && field.value.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {field.value.map((unityId: number) => {
                              const unityLabel = unities.find(
                                (u) => u.id === unityId
                              )?.name;
                              return (
                                <Badge
                                  key={unityId}
                                  variant="secondary"
                                  className="flex items-center gap-1"
                                >
                                  {unityLabel}
                                  <X
                                    className="h-3 w-3 cursor-pointer hover:text-red-500"
                                    onClick={() => {
                                      const newValue = field.value.filter(
                                        (id: number) => id !== unityId
                                      );
                                      field.onChange(newValue);
                                    }}
                                  />
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                        <Select
                          onValueChange={(value) => {
                            if (value && value !== "none") {
                              const unityId = Number(value);
                              const currentValues = field.value || [];
                              if (!currentValues.includes(unityId)) {
                                field.onChange([...currentValues, unityId]);
                              }
                            }
                          }}
                          value=""
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione as unidades" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              Todas as unidades
                            </SelectItem>
                            {unities.map((unity) => {
                              const isSelected = field.value?.includes(
                                unity.id
                              );
                              return (
                                <SelectItem
                                  key={unity.id}
                                  value={unity.id.toString()}
                                  disabled={isSelected}
                                >
                                  {unity.name} {isSelected && "(Selecionado)"}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="sale_type_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Tipos de Venda</Form.Label>
                        <div className="space-y-2">
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((typeId: number) => {
                                const typeLabel = saleTypes.find(
                                  (t) => t.id === typeId
                                )?.name;
                                return (
                                  <Badge
                                    key={typeId}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {typeLabel}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (id: number) => id !== typeId
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const typeId = Number(value);
                                const currentValues = field.value || [];
                                if (!currentValues.includes(typeId)) {
                                  field.onChange([...currentValues, typeId]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione os tipos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                Todos os tipos
                              </SelectItem>
                              {saleTypes.map((type) => {
                                const isSelected = field.value?.includes(
                                  type.id
                                );
                                return (
                                  <SelectItem
                                    key={type.id}
                                    value={type.id.toString()}
                                    disabled={isSelected}
                                  >
                                    {type.name} {isSelected && "(Selecionado)"}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="payment_form_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Formas de Pagamento</Form.Label>
                        <div className="space-y-2">
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((formId: number) => {
                                const formLabel = paymentForms.find(
                                  (f) => f.id === formId
                                )?.name;
                                return (
                                  <Badge
                                    key={formId}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {formLabel}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (id: number) => id !== formId
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const formId = Number(value);
                                const currentValues = field.value || [];
                                if (!currentValues.includes(formId)) {
                                  field.onChange([...currentValues, formId]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione as formas" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                Todas as formas
                              </SelectItem>
                              {paymentForms.map((form) => {
                                const isSelected = field.value?.includes(
                                  form.id
                                );
                                return (
                                  <SelectItem
                                    key={form.id}
                                    value={form.id.toString()}
                                    disabled={isSelected}
                                  >
                                    {form.name} {isSelected && "(Selecionado)"}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>
            </div>
          )}

          {isSalesReport && (
            <>
              <Form.Row>
                <Form.Field name="lottery_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>Loteria</Form.Label>
                      <div className="space-y-2">
                        {field.value && field.value.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {field.value.map((lotteryId: number) => {
                              const lotteryLabel = lotteries.find(
                                (l) => l.id === lotteryId
                              )?.name;
                              return (
                                <Badge
                                  key={lotteryId}
                                  variant="secondary"
                                  className="flex items-center gap-1"
                                >
                                  {lotteryLabel}
                                  <X
                                    className="h-3 w-3 cursor-pointer hover:text-red-500"
                                    onClick={() => {
                                      const newValue = field.value.filter(
                                        (id: number) => id !== lotteryId
                                      );
                                      field.onChange(newValue);
                                    }}
                                  />
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                        <Select
                          onValueChange={(value) => {
                            if (value && value !== "none") {
                              const lotteryId = Number(value);
                              const currentValues = field.value || [];
                              if (!currentValues.includes(lotteryId)) {
                                field.onChange([...currentValues, lotteryId]);
                              }
                            }
                          }}
                          value=""
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione as loterias desejadas" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              Todas as loterias
                            </SelectItem>
                            {lotteries.map((lottery) => {
                              const isSelected = field.value?.includes(
                                lottery.id
                              );
                              return (
                                <SelectItem
                                  key={lottery.id}
                                  value={lottery.id.toString()}
                                  disabled={isSelected}
                                >
                                  {lottery.name} {isSelected && "(Selecionado)"}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <Form.Row>
                <Form.Field name="unity_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>Unidade</Form.Label>
                      <div className="space-y-2">
                        {/* Badges para mostrar seleções */}
                        {field.value && field.value.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {field.value.map((unityId: number) => {
                              const unityLabel = unities.find(
                                (u) => u.id === unityId
                              )?.name;
                              return (
                                <Badge
                                  key={unityId}
                                  variant="secondary"
                                  className="flex items-center gap-1"
                                >
                                  {unityLabel}
                                  <X
                                    className="h-3 w-3 cursor-pointer hover:text-red-500"
                                    onClick={() => {
                                      const newValue = field.value.filter(
                                        (id: number) => id !== unityId
                                      );
                                      field.onChange(newValue);
                                    }}
                                  />
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                        {/* Select para adicionar novas unidades */}
                        <Select
                          onValueChange={(value) => {
                            if (value && value !== "none") {
                              const unityId = Number(value);
                              const currentValues = field.value || [];
                              if (!currentValues.includes(unityId)) {
                                field.onChange([...currentValues, unityId]);
                              }
                            }
                          }}
                          value=""
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione as unidades desejadas" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              Todas as unidades
                            </SelectItem>
                            {unities.map((unity) => {
                              const isSelected = field.value?.includes(
                                unity.id
                              );
                              return (
                                <SelectItem
                                  key={unity.id}
                                  value={unity.id.toString()}
                                  disabled={isSelected}
                                >
                                  {unity.name} {isSelected && "(Selecionado)"}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="sale_type_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Tipo de Venda</Form.Label>
                        <div className="space-y-2">
                          {/* Badges para mostrar seleções */}
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((typeId: number) => {
                                const typeLabel = saleTypes.find(
                                  (t) => t.id === typeId
                                )?.name;
                                return (
                                  <Badge
                                    key={typeId}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {typeLabel}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (id: number) => id !== typeId
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          {/* Select para adicionar novos tipos de venda */}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const typeId = Number(value);
                                const currentValues = field.value || [];
                                if (!currentValues.includes(typeId)) {
                                  field.onChange([...currentValues, typeId]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione os tipos de venda desejados" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                Todos os tipos
                              </SelectItem>
                              {saleTypes.map((type) => {
                                const isSelected = field.value?.includes(
                                  type.id
                                );
                                return (
                                  <SelectItem
                                    key={type.id}
                                    value={type.id.toString()}
                                    disabled={isSelected}
                                  >
                                    {type.name} {isSelected && "(Selecionado)"}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="payment_form_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Forma de Pagamento</Form.Label>
                        <div className="space-y-2">
                          {/* Badges para mostrar seleções */}
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((formId: number) => {
                                const formLabel = paymentForms.find(
                                  (f) => f.id === formId
                                )?.name;
                                return (
                                  <Badge
                                    key={formId}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {formLabel}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (id: number) => id !== formId
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          {/* Select para adicionar novas formas de pagamento */}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const formId = Number(value);
                                const currentValues = field.value || [];
                                if (!currentValues.includes(formId)) {
                                  field.onChange([...currentValues, formId]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione as formas de pagamento desejadas" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                Todas as formas
                              </SelectItem>
                              {paymentForms.map((form) => {
                                const isSelected = field.value?.includes(
                                  form.id
                                );
                                return (
                                  <SelectItem
                                    key={form.id}
                                    value={form.id.toString()}
                                    disabled={isSelected}
                                  >
                                    {form.name} {isSelected && "(Selecionado)"}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="seller_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Vendedor</Form.Label>
                        <div className="space-y-2">
                          {/* Badges para mostrar seleções */}
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((seller) => {
                                return (
                                  <Badge
                                    key={seller}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {seller?.user?.name}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (data) => data.id !== seller.id
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}

                          {/* Select para adicionar novos vendedores */}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const currentValues = field.value || [];
                                if (
                                  !currentValues.includes(
                                    sellers.find(
                                      (s) => s.id.toString() === value
                                    )
                                  )
                                ) {
                                  field.onChange([
                                    ...currentValues,
                                    sellers.find(
                                      (s) => s.id.toString() === value
                                    ),
                                  ]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione os vendedores desejados" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                Todos os vendedores
                              </SelectItem>
                              {sellers.map((seller) => (
                                <SelectItem
                                  key={seller.id}
                                  value={seller.id.toString()}
                                >
                                  {seller?.user?.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="sale_status_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Status da Venda</Form.Label>
                        <div className="space-y-2">
                          {/* Badges para mostrar seleções */}
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((statusId: number) => {
                                const statusLabel =
                                  SALE_STATUS_LABELS[statusId];
                                return (
                                  <Badge
                                    key={statusId}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {statusLabel}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (id: number) => id !== statusId
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}

                          {/* Select para adicionar novos status */}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const statusId = Number(value);
                                const currentValues = field.value || [];
                                if (!currentValues.includes(statusId)) {
                                  field.onChange([...currentValues, statusId]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione os status desejados" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(SALE_STATUS_LABELS).map(
                                ([id, label]) => {
                                  const statusId = Number(id);
                                  const isSelected =
                                    field.value?.includes(statusId);
                                  return (
                                    <SelectItem
                                      key={id}
                                      value={id}
                                      disabled={isSelected}
                                    >
                                      {label} {isSelected && "(Selecionado)"}
                                    </SelectItem>
                                  );
                                }
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>
            </>
          )}

          {/* Filtros para Compras Clientes */}
          {isCustomerPurchasesReport && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="seller_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Vendedor</Form.Label>
                        <div className="space-y-2">
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((seller) => {
                                return (
                                  <Badge
                                    key={seller}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {seller?.user?.name}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (data) => data.id !== seller.id
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const currentValues = field.value || [];
                                if (
                                  !currentValues.includes(
                                    sellers.find(
                                      (s) => s.id.toString() === value
                                    )
                                  )
                                ) {
                                  field.onChange([
                                    ...currentValues,
                                    sellers.find(
                                      (s) => s.id.toString() === value
                                    ),
                                  ]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione os vendedores..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                Todos os vendedores
                              </SelectItem>
                              {sellers.map((seller) => (
                                <SelectItem
                                  key={seller.id}
                                  value={seller.id.toString()}
                                >
                                  {seller?.user?.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="lottery_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Loteria</Form.Label>
                        <div className="space-y-2">
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((lotteryId: number) => {
                                const lotteryLabel = lotteries.find(
                                  (l) => l.id === lotteryId
                                )?.name;
                                return (
                                  <Badge
                                    key={lotteryId}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {lotteryLabel}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (id: number) => id !== lotteryId
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const lotteryId = Number(value);
                                const currentValues = field.value || [];
                                if (!currentValues.includes(lotteryId)) {
                                  field.onChange([...currentValues, lotteryId]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione as loterias desejadas" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                Todas as loterias
                              </SelectItem>
                              {lotteries.map((lottery) => {
                                const isSelected = field.value?.includes(
                                  lottery.id
                                );
                                return (
                                  <SelectItem
                                    key={lottery.id}
                                    value={lottery.id.toString()}
                                    disabled={isSelected}
                                  >
                                    {lottery.name} {isSelected && "(Selecionado)"}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="sale_type_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Tipo de Venda</Form.Label>
                        <div className="space-y-2">
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((typeId: number) => {
                                const typeLabel = saleTypes.find(
                                  (t) => t.id === typeId
                                )?.name;
                                return (
                                  <Badge
                                    key={typeId}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {typeLabel}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (id: number) => id !== typeId
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const typeId = Number(value);
                                const currentValues = field.value || [];
                                if (!currentValues.includes(typeId)) {
                                  field.onChange([...currentValues, typeId]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione os tipos de venda..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                Todos os tipos
                              </SelectItem>
                              {saleTypes.map((type) => {
                                const isSelected = field.value?.includes(
                                  type.id
                                );
                                return (
                                  <SelectItem
                                    key={type.id}
                                    value={type.id.toString()}
                                    disabled={isSelected}
                                  >
                                    {type.name} {isSelected && "(Selecionado)"}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="unity_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Unidade</Form.Label>
                        <div className="space-y-2">
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((unityId: number) => {
                                const unityLabel = unities.find(
                                  (u) => u.id === unityId
                                )?.name;
                                return (
                                  <Badge
                                    key={unityId}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {unityLabel}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (id: number) => id !== unityId
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const unityId = Number(value);
                                const currentValues = field.value || [];
                                if (!currentValues.includes(unityId)) {
                                  field.onChange([...currentValues, unityId]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione as unidades desejadas" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                Todas as unidades
                              </SelectItem>
                              {unities.map((unity) => {
                                const isSelected = field.value?.includes(
                                  unity.id
                                );
                                return (
                                  <SelectItem
                                    key={unity.id}
                                    value={unity.id.toString()}
                                    disabled={isSelected}
                                  >
                                    {unity.name} {isSelected && "(Selecionado)"}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="sale_status_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Status da Venda</Form.Label>
                        <div className="space-y-2">
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {field.value.map((statusId: number) => {
                                const statusLabel =
                                  SALE_STATUS_LABELS[statusId];
                                return (
                                  <Badge
                                    key={statusId}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {statusLabel}
                                    <X
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const newValue = field.value.filter(
                                          (id: number) => id !== statusId
                                        );
                                        field.onChange(newValue);
                                      }}
                                    />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          <Select
                            onValueChange={(value) => {
                              if (value && value !== "none") {
                                const statusId = Number(value);
                                const currentValues = field.value || [];
                                if (!currentValues.includes(statusId)) {
                                  field.onChange([...currentValues, statusId]);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione os status desejados" />
                            </SelectTrigger>
                            <SelectContent>
                              {CUSTOMER_PURCHASES_STATUS_OPTIONS.map(
                                (status) => {
                                  const isSelected =
                                    field.value?.includes(status.id);
                                  return (
                                    <SelectItem
                                      key={status.id}
                                      value={status.id.toString()}
                                      disabled={isSelected}
                                    >
                                      {status.label} {isSelected && "(Selecionado)"}
                                    </SelectItem>
                                  );
                                }
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="client_type">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>Tipo de Cliente</Form.Label>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || 'all'}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="new">Novos</SelectItem>
                            <SelectItem value="old">Antigos</SelectItem>
                          </SelectContent>
                        </Select>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>
            </>
          )}

          {/* Filtros para Devoluções */}
          {isRefundsReport && (
            <div className="space-y-4">
              <h3 className="font-semibold mb-3">Filtros de Devolução</h3>
              <Form.Row>
                <Form.Field name="devolution_status_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>Status da Devolução</Form.Label>
                      <div className="space-y-2">
                        {field.value && field.value.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {field.value.map((statusId: number) => {
                              const statusLabel = DEVOLUTION_STATUS_OPTIONS.find(
                                (s) => s.id === statusId
                              )?.name;
                              return (
                                <Badge
                                  key={statusId}
                                  variant="secondary"
                                  className="flex items-center gap-1"
                                >
                                  {statusLabel}
                                  <X
                                    className="h-3 w-3 cursor-pointer hover:text-red-500"
                                    onClick={() => {
                                      const newValue = field.value.filter(
                                        (id: number) => id !== statusId
                                      );
                                      field.onChange(newValue);
                                    }}
                                  />
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                        <Select
                          onValueChange={(value) => {
                            if (value && value !== "none") {
                              const statusId = Number(value);
                              const currentValues = field.value || [];
                              if (!currentValues.includes(statusId)) {
                                field.onChange([...currentValues, statusId]);
                              }
                            }
                          }}
                          value=""
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione os status desejados" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              Todos os status
                            </SelectItem>
                            {DEVOLUTION_STATUS_OPTIONS.map((status) => {
                              const isSelected = field.value?.includes(status.id);
                              return (
                                <SelectItem
                                  key={status.id}
                                  value={status.id.toString()}
                                  disabled={isSelected}
                                >
                                  {status.name} {isSelected && "(Selecionado)"}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </div>
          )}

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Gerando relatório <LoaderSpin />
                </>
              )}
              {formState === "default" && "Gerar"}
              {formState === "error" && "Erro ao gerar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
